import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';

// ── Spec rows to show on the sheet ──────────────────────────────
const SPEC_ROWS: { types: string[]; label: string }[] = [
  { types: ['cpu'], label: 'CPU' },
  { types: ['ram'], label: 'RAM' },
  { types: ['rom', 'rom_1', 'rom_2', 'rom_3', 'storage'], label: 'Storage' },
  { types: ['gpu'], label: 'Graphics' },
  { types: ['psu'], label: 'Power Supply' },
  { types: ['case', 'cas'], label: 'Case' },
  { types: ['os'], label: 'Operating System' },
];

interface Props {
  build: {
    id: string;
    sku: string;
    description?: string;
  };
  activeConfigSkus?: Set<string> | null;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'icon';
  className?: string;
}

export function PrintSpecSheet({
  build,
  activeConfigSkus,
  variant = 'outline',
  size = 'sm',
  className,
}: Props) {
  const [printing, setPrinting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [wifi, setWifi] = useState(false);
  const [bluetooth, setBluetooth] = useState(false);
  const [selections, setSelections] = useState<Record<string, string[]>>({});

  // ── Fetch BOM (same fallback as BuildDetailPage) ───────────────
  const { data: bomRows } = useQuery({
    queryKey: ['spec-bom', build.id, build.sku],
    queryFn: async () => {
      const { data: bcRows } = await supabase
        .from('build_components')
        .select('component_sku, component_type, qty_per_build')
        .eq('build_id', build.id);

      if (bcRows && bcRows.length > 0) {
        return bcRows.map(r => ({ sku: r.component_sku, type: r.component_type || '' }));
      }

      if (!build.sku) return [];
      const { data } = await supabase
        .from('components')
        .select('part_number, description, category, build_sku')
        .ilike('build_sku', `%${build.sku}%`);

      return (data ?? [])
        .filter(c => {
          const entries = (c.build_sku ?? '').split(',').map((s: string) => s.trim().replace(/-EOL$/i, ''));
          return entries.includes(build.sku);
        })
        .map(c => ({ sku: c.part_number, type: c.category || '' }));
    },
    enabled: !!build.id,
  });

  // ── Fetch display names + descriptions ─────────────────────────
  const componentSkus = useMemo(
    () => (bomRows || []).map(r => r.sku).filter(Boolean),
    [bomRows],
  );

  const { data: nameMap = {} } = useQuery({
    queryKey: ['spec-names', componentSkus],
    queryFn: async () => {
      if (componentSkus.length === 0) return {};
      const [dnRes, descRes] = await Promise.all([
        supabase
          .from('component_display_names')
          .select('part_number, display_name_b2c')
          .in('part_number', componentSkus),
        supabase
          .from('components')
          .select('part_number, description')
          .in('part_number', componentSkus),
      ]);
      const map: Record<string, string> = {};
      (descRes.data ?? []).forEach((r: any) => {
        if (r.description) map[r.part_number] = r.description;
      });
      (dnRes.data ?? []).forEach((r: any) => {
        if (r.display_name_b2c) map[r.part_number] = r.display_name_b2c;
      });
      return map;
    },
    enabled: componentSkus.length > 0,
  });

  // ── Group BOM by category ──────────────────────────────────────
  const categoryGroups = useMemo(() => {
    if (!bomRows || bomRows.length === 0) return [];
    return SPEC_ROWS.map(cat => {
      const matching = bomRows.filter(r =>
        cat.types.includes((r.type || '').toLowerCase()),
      );
      return {
        label: cat.label,
        options: matching.map(r => ({
          sku: r.sku,
          name: nameMap[r.sku] || r.sku,
        })),
      };
    });
  }, [bomRows, nameMap]);

  const hasMultiple = categoryGroups.some(g => g.options.length > 1);
  const isStorageCategory = (label: string) => label === 'Storage';

  const getDefaultSelections = (): Record<string, string[]> => {
    const result: Record<string, string[]> = {};
    categoryGroups.forEach(cat => {
      if (cat.options.length <= 1) return;
      if (activeConfigSkus) {
        const matching = cat.options.filter(o => activeConfigSkus.has(o.sku));
        if (matching.length > 0) {
          result[cat.label] = matching.map(o => o.sku);
          return;
        }
      }
      if (isStorageCategory(cat.label)) {
        result[cat.label] = cat.options.map(o => o.sku);
      } else {
        result[cat.label] = [cat.options[0].sku];
      }
    });
    return result;
  };

  // ── Resolve final specs ────────────────────────────────────────
  const getResolvedSpecs = () => {
    const sel = Object.keys(selections).length > 0 ? selections : getDefaultSelections();
    return categoryGroups.map(cat => {
      if (cat.options.length === 0) return { label: cat.label, value: cat.label === 'Operating System' ? 'No License' : '—' };
      if (cat.options.length === 1) return { label: cat.label, value: cat.options[0].name };
      const selectedSkus = sel[cat.label] || [cat.options[0].sku];
      const selected = cat.options.filter(o => selectedSkus.includes(o.sku));
      if (selected.length === 0) return { label: cat.label, value: cat.options[0].name };
      return { label: cat.label, value: selected.map(s => s.name).join(' + ') };
    });
  };

  // ── Open dialog ────────────────────────────────────────────────
  const handleClick = () => {
    setSelections(getDefaultSelections());
    setDialogOpen(true);
  };

  // ── Generate & print ───────────────────────────────────────────
  const doPrint = () => {
    setPrinting(true);
    setDialogOpen(false);

    const specs = getResolvedSpecs();

    const specRowsHtml = specs.map(s => `
      <tr>
        <td class="label">${s.label}</td>
        <td class="value">${s.value}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <title>Spec Sheet — ${build.sku}</title>
  <style>
    @page { size: A4 portrait; margin: 20mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12pt; color: #111;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .header {
      display: flex; align-items: center; gap: 16px;
      margin-bottom: 24px; padding-bottom: 16px;
      border-bottom: 2px solid #111;
    }
    .header img { height: 50px; width: auto; }
    .title { font-size: 20pt; font-weight: 700; }
    table {
      width: 100%; border-collapse: collapse;
      margin-top: 16px;
    }
    th, td {
      padding: 10px 14px;
      border: 1px solid #ddd;
      text-align: left;
      vertical-align: middle;
    }
    thead th {
      background: #f5f5f5;
      font-weight: 700;
      font-size: 11pt;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
    .label {
      font-weight: 600;
      width: 140px;
      white-space: nowrap;
      background: #fafafa;
    }
    .value { font-weight: 400; }
  </style>
</head>
<body>
  <div class="header">
    <img src="/logo-horizontal.png" alt="AOI" />
    <div class="title">${build.description || build.sku}</div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Specification</th>
        <th>Details</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td class="label">SKU</td>
        <td class="value" style="font-family:monospace;font-weight:600;">${build.sku}</td>
      </tr>
      ${specRowsHtml}
      <tr>
        <td class="label">WiFi</td>
        <td class="value">${wifi ? 'Yes' : 'No'}</td>
      </tr>
      <tr>
        <td class="label">Bluetooth</td>
        <td class="value">${bluetooth ? 'Yes' : 'No'}</td>
      </tr>
    </tbody>
  </table>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=800,height=1000');
    if (!printWindow) { setPrinting(false); return; }

    printWindow.document.write(html);
    printWindow.document.close();

    const images = printWindow.document.querySelectorAll('img');
    let loaded = 0;
    const total = images.length;
    let printed = false;

    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        setPrinting(false);
      }, 300);
    };

    if (total === 0) setTimeout(triggerPrint, 100);
    else {
      images.forEach(img => {
        img.onload = img.onerror = () => { loaded++; if (loaded >= total) setTimeout(triggerPrint, 250); };
      });
      setTimeout(triggerPrint, 3000);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        disabled={printing}
        onClick={handleClick}
      >
        <FileDown className="h-4 w-4 mr-1.5" />
        {printing ? 'Generating...' : 'Spec Sheet'}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Spec Sheet Options</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* WiFi / Bluetooth toggles */}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wifi}
                  onChange={e => setWifi(e.target.checked)}
                  className="h-4 w-4 rounded"
                />
                <span className="text-sm font-medium">WiFi</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bluetooth}
                  onChange={e => setBluetooth(e.target.checked)}
                  className="h-4 w-4 rounded"
                />
                <span className="text-sm font-medium">Bluetooth</span>
              </label>
            </div>

            {/* Component selection for categories with multiple options */}
            {hasMultiple && categoryGroups.map(cat => {
              if (cat.options.length <= 1) return null;
              const isMulti = isStorageCategory(cat.label);
              const selected = selections[cat.label] || [];
              return (
                <div key={cat.label}>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">
                    {cat.label}{isMulti ? ' (select all that apply)' : ''}
                  </label>
                  <div className="space-y-1">
                    {cat.options.map(opt => {
                      const isChecked = selected.includes(opt.sku);
                      return (
                        <label
                          key={opt.sku}
                          className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors ${
                            isChecked
                              ? 'border-primary bg-primary/5'
                              : 'border-muted hover:bg-muted/50'
                          }`}
                        >
                          <input
                            type={isMulti ? 'checkbox' : 'radio'}
                            name={`spec-${cat.label}`}
                            checked={isChecked}
                            onChange={() => {
                              if (isMulti) {
                                setSelections(prev => {
                                  const cur = prev[cat.label] || [];
                                  const next = isChecked
                                    ? cur.filter(s => s !== opt.sku)
                                    : [...cur, opt.sku];
                                  return { ...prev, [cat.label]: next };
                                });
                              } else {
                                setSelections(prev => ({ ...prev, [cat.label]: [opt.sku] }));
                              }
                            }}
                            className="mt-0.5"
                          />
                          <div className="text-sm">
                            <div className="font-medium">{opt.name}</div>
                            <div className="text-xs text-muted-foreground font-mono">{opt.sku}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={doPrint}>
              <FileDown className="h-4 w-4 mr-1.5" />Generate Sheet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
